import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { UseHasPermission } from "lib/hooks/useHasPermission";
import { UseUserRoles } from "lib/hooks/useUserRoles";
import { FormDropdownFilter } from "components/form/FormDropDownFilter";
import * as filterEnum from "enums/filterEnum";
import { FormCardContext, ErrorMessage } from "components";
import {
	Control,
	UseFormSetValue,
	UseFormTrigger,
} from "react-hook-form";
import type { UseFormWatch } from "react-hook-form";
import { ROLE_NAME_SECTOR_ADMIN } from "../../constants";

export interface SectorOption {
	id: string;
	name: string;
}

export interface SectorDropdownProps {
	control: Control<any>;
	name: string;
	watch: UseFormWatch<any>;
	setValue: UseFormSetValue<any>;
	trigger?: UseFormTrigger<any>;
	disabled?: boolean;
	required?: boolean;
}

/**
 * Shared sector dropdown component for Add/Edit Sector Admin dialogs.
 * Provides consistent styling, validation, and behavior.
 */
export function SectorDropdown({
	control,
	name,
	setValue,
	trigger,
	disabled = false,
	required = true,
}: SectorDropdownProps) {
	const permissions = UseHasPermission();
	const { allRoles } = UseUserRoles();

	// Get accessible sectors using UseHasPermission for efficient filtering
	// This leverages the pre-computed accessibleSectorIdsAsAdminSet for O(1) lookups
	const accessibleSectors = useMemo(() => {
		const sectorMap = new Map<string, SectorOption>();
		const adminSectorIds = permissions.accessibleSectorIdsAsAdminSet;

		allRoles.forEach((role) => {
			if (
				role.containmentSectorId &&
				role.containmentSectorName &&
				adminSectorIds.has(role.containmentSectorId) &&
				!sectorMap.has(role.containmentSectorId)
			) {
				sectorMap.set(role.containmentSectorId, {
					id: role.containmentSectorId,
					name: role.containmentSectorName,
				});
			}
		});

		return Array.from(sectorMap.values()).sort((a, b) =>
			a.name.localeCompare(b.name)
		);
	}, [permissions.accessibleSectorIdsAsAdminSet, allRoles]);

	return (
		<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
			<Typography variant="body1" sx={{ minWidth: 80 }}>
				Sector:
			</Typography>
			<FormCardContext.Provider value={{ hideLabel: true }}>
				<Box data-test-selector="sectors-dropdown" sx={{ minWidth: 200, flex: "1 1 auto" }}>
					<FormDropdownFilter
						control={control}
						name={name}
						required={required}
						multiple
						filterType={filterEnum.Dropdown<
							"sectors",
							SectorOption
						>({
							field: "sectors",
							getter: async () => accessibleSectors,
							label: "Sectors",
						})}
						staticOptions={accessibleSectors}
						autoTrigger
						keyFunc={(s) => s.id}
						labelFunc={(s) => s.name}
						onChange={({ field, value }) => {
							if (value) {
								setValue(
									field,
									Array.isArray(value) ? value : [value],
									{ shouldValidate: true }
								);
							} else {
								// Handle empty value - set to empty array and trigger validation
								setValue(field, [], { shouldValidate: true });
							}
							// Trigger validation after setting value
							if (trigger) {
								trigger(name as any);
							}
						}}
						rules={
							required
								? {
										validate: (val) =>
											Array.isArray(val)
												? !val.IsEmpty() ||
													"At least one sector must be selected"
												: true,
									}
								: undefined
						}
						renderError={(err) => {
							return err
								.IfErr((msg) => <ErrorMessage msg={msg} />)
								.UnwrapOrNil();
						}}
						disabled={disabled}
					/>
				</Box>
			</FormCardContext.Provider>
		</Box>
	);
}
